import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Alert,
  CircularProgress,
  Skeleton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Description as MarkdownIcon,
  PictureAsPdf as PdfIcon,
  Article as DocxIcon,
  Code as HtmlIcon,
  TextSnippet as TxtIcon
} from '@mui/icons-material';
import { Accordion, ScrollableBox } from '../atoms';
import MarkdownRenderer from '../common/MarkdownRenderer';
import useNotification from '../../hooks/useNotification';
import useValidation from '../../hooks/useValidation';
import useImprove from '../../hooks/useImprove';
import conversationService from '../../services/conversationService';
import api from '../../services/api';
import documentExportService from '../../services/documentExportService';
import axios from 'axios';
import SectionHeader from '../atoms/Header';
import ActionButtons from './ActionButtons';
import ConfirmationDialog from '../common/ConfirmationDialog';
import ImprovementModal from '../atoms/ImprovementModal';
import InlineImproveInput from '../common/InlineImproveInput';
import VersionSelector from '../atoms/VersionSelector';
import useProjectInfoVersions, { parseProjectInfoSections } from '../../hooks/useProjectInfoVersions';
import useFullScreen from '../../hooks/useFullScreen';
import { ValidatedIcon, UnvalidatedIcon } from '../icons/ValidationIcons';
import FullScreenButton from '../common/FullScreenButton';
import PermissionGate from '../common/PermissionGate';
import useParsedSections from '../../hooks/useParsedSections';

// Get auth token from localStorage
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// =============================================================================
// MANDATORY SECTION DEFINITIONS
// These define the EXACT structure that ALL rendering MUST use.
// This ensures consistency across: conversation, generate, re-generate, import
// =============================================================================

// Full mandatory sections for feature/project information
// Matches STANDARD_SECTIONS['new-feature'] from backend
const MANDATORY_SECTIONS = [
  { id: 'executive-summary', title: 'Executive Summary', description: 'High-level feature overview and key points' },
  { id: 'feature-vision', title: 'Feature Vision', description: 'Mission, vision, and strategic alignment with the parent project' },
  { id: 'business-case', title: 'Business Case', description: 'Problem statement, opportunity, and expected outcomes' },
  { id: 'target-audience', title: 'Target Audience', description: 'Detailed user personas and market analysis' },
  { id: 'competitive-analysis', title: 'Competitive Analysis', description: 'Market landscape and positioning' },
  { id: 'success-metrics', title: 'Success Metrics', description: 'KPIs, goals, and measurement framework' },
  { id: 'feature-scope', title: 'Feature Scope', description: 'Inclusions, exclusions, and boundaries' },
  { id: 'integration-points', title: 'Integration Points', description: 'How this feature connects with existing functionality' },
  { id: 'dependencies', title: 'Dependencies', description: 'Existing features, APIs, or data this feature relies on' },
  { id: 'constraints-assumptions', title: 'Constraints & Assumptions', description: 'Limitations and key assumptions' },
  { id: 'timeline-milestones', title: 'Timeline & Milestones', description: 'High-level schedule and deliverables' },
  { id: 'risk-assessment', title: 'Risk Assessment', description: 'Potential risks and mitigation strategies' },
];

// Skeleton sections for loading state
const SKELETON_SECTIONS = MANDATORY_SECTIONS.map(s => ({ key: s.id, title: s.title }));

/**
 * Normalize section title to match canonical titles
 * Maps various AI-generated titles to our standard section titles
 */
const normalizeSectionTitle = (title) => {
  if (!title) return title;

  const titleLower = title.toLowerCase().trim();

  // Map common variations to canonical titles
  const titleMappings = {
    // Executive Summary variations
    'executive summary': 'Executive Summary',
    'summary': 'Executive Summary',
    'overview': 'Executive Summary',
    'project overview': 'Executive Summary',
    'feature overview': 'Executive Summary',

    // Feature/Project Vision variations
    'feature vision': 'Feature Vision',
    'project vision': 'Feature Vision',
    'vision': 'Feature Vision',
    'mission': 'Feature Vision',
    'mission & vision': 'Feature Vision',

    // Business Case variations
    'business case': 'Business Case',
    'business justification': 'Business Case',
    'problem statement': 'Business Case',
    'opportunity': 'Business Case',

    // Target Audience variations
    'target audience': 'Target Audience',
    'target users': 'Target Audience',
    'user personas': 'Target Audience',
    'audience': 'Target Audience',
    'users': 'Target Audience',

    // Competitive Analysis variations
    'competitive analysis': 'Competitive Analysis',
    'competition': 'Competitive Analysis',
    'market analysis': 'Competitive Analysis',
    'competitor analysis': 'Competitive Analysis',

    // Success Metrics variations
    'success metrics': 'Success Metrics',
    'kpis': 'Success Metrics',
    'key performance indicators': 'Success Metrics',
    'metrics': 'Success Metrics',
    'goals': 'Success Metrics',

    // Feature/Project Scope variations
    'feature scope': 'Feature Scope',
    'project scope': 'Feature Scope',
    'scope': 'Feature Scope',
    'in scope': 'Feature Scope',
    'inclusions': 'Feature Scope',

    // Integration Points variations
    'integration points': 'Integration Points',
    'integrations': 'Integration Points',
    'integration': 'Integration Points',
    'system integration': 'Integration Points',

    // Dependencies variations
    'dependencies': 'Dependencies',
    'dependency': 'Dependencies',
    'prerequisites': 'Dependencies',
    'requirements': 'Dependencies',

    // Constraints & Assumptions variations
    'constraints & assumptions': 'Constraints & Assumptions',
    'constraints and assumptions': 'Constraints & Assumptions',
    'constraints': 'Constraints & Assumptions',
    'assumptions': 'Constraints & Assumptions',
    'limitations': 'Constraints & Assumptions',

    // Timeline & Milestones variations
    'timeline & milestones': 'Timeline & Milestones',
    'timeline and milestones': 'Timeline & Milestones',
    'timeline': 'Timeline & Milestones',
    'milestones': 'Timeline & Milestones',
    'schedule': 'Timeline & Milestones',
    'roadmap': 'Timeline & Milestones',
    'project timeline': 'Timeline & Milestones',
    'delivery schedule': 'Timeline & Milestones',
    'project schedule': 'Timeline & Milestones',
    'implementation timeline': 'Timeline & Milestones',
    'key milestones': 'Timeline & Milestones',
    'deliverables': 'Timeline & Milestones',
    'phases': 'Timeline & Milestones',
    'project phases': 'Timeline & Milestones',

    // Risk Assessment variations
    'risk assessment': 'Risk Assessment',
    'risks': 'Risk Assessment',
    'risk analysis': 'Risk Assessment',
    'risk considerations': 'Risk Assessment',
    'risk management': 'Risk Assessment',
    'potential risks': 'Risk Assessment',
    'risk mitigation': 'Risk Assessment',
    'risks and mitigation': 'Risk Assessment',
  };

  return titleMappings[titleLower] || title;
};

/**
 * Normalize parsed sections to ensure ALL mandatory sections exist
 * - Maps existing section titles to canonical titles
 * - Adds missing sections with placeholder content
 * - Maintains consistent order
 *
 * This is applied at RENDERING stage only, NOT during version detection
 */
const normalizeSections = (parsedSections) => {
  if (!parsedSections) return [];

  // Create a map of existing sections by normalized title
  const existingSectionsMap = new Map();
  parsedSections.forEach((section, index) => {
    const normalizedTitle = normalizeSectionTitle(section.title);
    // Only store the first occurrence of each normalized title
    if (!existingSectionsMap.has(normalizedTitle)) {
      existingSectionsMap.set(normalizedTitle, {
        ...section,
        title: normalizedTitle, // Use canonical title
        originalTitle: section.title
      });
    }
  });

  // Placeholder messages for sections without content
  const placeholderMessages = {
    'Executive Summary': 'A comprehensive executive summary will be generated once project details are provided.',
    'Feature Vision': 'The feature vision and strategic alignment will be defined based on project objectives.',
    'Business Case': 'Business justification and expected outcomes will be documented during requirements gathering.',
    'Target Audience': 'User personas and target audience analysis will be developed based on market research.',
    'Competitive Analysis': 'Market landscape and competitive positioning will be analyzed during discovery.',
    'Success Metrics': 'KPIs and success criteria will be established based on business objectives.',
    'Feature Scope': 'Scope boundaries and deliverables will be defined during planning.',
    'Integration Points': 'Integration requirements will be identified based on system architecture.',
    'Dependencies': 'Technical and functional dependencies will be mapped during analysis.',
    'Constraints & Assumptions': 'Constraints and assumptions will be documented during stakeholder discussions.',
    'Timeline & Milestones': 'Project timeline and key milestones will be established during planning phase.',
    'Risk Assessment': 'Risk analysis and mitigation strategies will be developed during project planning.',
  };

  // Build normalized sections array with ALL mandatory sections
  const normalizedSections = MANDATORY_SECTIONS.map((mandatory, index) => {
    const existingSection = existingSectionsMap.get(mandatory.title);

    // Check if section exists AND has actual content (not empty/whitespace)
    const hasContent = existingSection &&
      existingSection.content &&
      existingSection.content.trim().length > 0;

    if (hasContent) {
      // Use existing content with canonical title and unique ID
      return {
        ...existingSection,
        id: `${mandatory.id}-${index}`,
        title: mandatory.title,
        category: null,
        isFirstInCategory: false
      };
    } else {
      // Create placeholder section - either no section found OR section has empty content
      return {
        id: `${mandatory.id}-${index}`,
        title: mandatory.title,
        content: placeholderMessages[mandatory.title] || 'Details to be determined.',
        category: null,
        isFirstInCategory: false
      };
    }
  });

  return normalizedSections;
};

// Skeleton Accordion Component for loading state - no text shown
const SkeletonAccordion = () => {
  return (
    <Box sx={{ mb: 2 }}>
      <Paper
        sx={{
          p: 2,
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <Skeleton variant="circular" width={24} height={24} />
          <Skeleton variant="text" width="60%" height={24} />
        </Box>
        <Skeleton variant="circular" width={20} height={20} />
      </Paper>
    </Box>
  );
};

// Loading Panel Component - shows skeleton accordions without text
const LoadingPanel = () => {
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Skeleton variant="text" width="60%" height={32} />
        <Skeleton variant="text" width="40%" height={20} />
      </Box>
      {SKELETON_SECTIONS.map((section) => (
        <SkeletonAccordion key={section.key} />
      ))}
    </Box>
  );
};

const DynamicProjectInfoAccordion = ({
  sections,
  expandedAccordion,
  onAccordionChange,
  isSectionValidated,
  onValidate,
  memberType,
  isAssignedValidator,
  getValidatorType,
  onEdit,
  onSave,
  onCancel,
  onImprove,
  onCopy,
  onDelete,
  editingSection,
  editedContent,
  setEditedContent,
  isSaving,
  // Improve props
  showImproveInput,
  improvePrompt,
  setImprovePrompt,
  onSendImprove,
  onCancelImprove
}) => {
  const { showNotification } = useNotification();

  if (!sections || sections.length === 0) {
    return (
      <Alert severity="info">
        No project information available yet. Start a conversation to generate project info.
      </Alert>
    );
  }

  return (
    <Box>
      {sections.map((section, index) => {
        const sectionKey = section.id || `section-${index}`;
        const isExpanded = expandedAccordion === sectionKey;
        const isEditing = editingSection === sectionKey;
        // Use the function to check validation status (version-specific)
        const isValidated = isSectionValidated(sectionKey);

        return (
          <React.Fragment key={sectionKey}>
            {/* Display category header if this is the first section in a category */}
            {section.isFirstInCategory && section.category && (
              <Box sx={{
                mt: index === 0 ? 0 : 3,
                mb: 2,
                px: 2
              }}>
                <SectionHeader label={section.category} />
              </Box>
            )}
            <Accordion
              title={section.title}
              expanded={isExpanded}
              onChange={() => onAccordionChange(sectionKey)}
              rightIcon={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {/* Validate toggle button - click to validate/un-validate */}
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onValidate(sectionKey, section.title);
                    }}
                    title={isValidated ? "Click to un-validate" : "Click to validate"}
                  >
                    {isValidated ? (
                      <ValidatedIcon fontSize="small" memberType={getValidatorType ? getValidatorType(sectionKey) : memberType} sx={{ fontSize: '1.2rem', color: 'success.main' }} />
                    ) : (
                      <UnvalidatedIcon fontSize="small" sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
                    )}
                  </IconButton>
                </Box>
              }
              sx={{ my: 2 }}
            >
              {/* Content with custom scrollbar styling */}
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                width: '93%',
                textAlign: 'justify',
                minHeight: '160px',
                maxHeight: 'calc(100vh - 350px)',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-track': {
                  borderRadius: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  marginTop: '5px',
                  marginBottom: '5px',
                },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.4)',
                  },
                },
                '&::-webkit-scrollbar-corner': {
                  backgroundColor: 'transparent',
                },
              }}>
                {isEditing ? (
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      '& textarea': {
                        width: '100%',
                        minHeight: '200px',
                        padding: '12px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        fontFamily: 'inherit',
                        fontSize: '14px',
                        resize: 'vertical',
                        outline: 'none',
                        '&:focus': {
                          border: '1px solid #485869'
                        }
                      }
                    }}
                  >
                    <textarea
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                    />
                  </Box>
                ) : (
                  <Box>
                    <MarkdownRenderer content={section.content || ''} />
                    {/* Inline improve input - shows when this section is being improved */}
                    {showImproveInput === sectionKey && (
                      <InlineImproveInput
                        improvePrompt={improvePrompt}
                        onPromptChange={setImprovePrompt}
                        onSend={() => onSendImprove(section)}
                        onCancel={onCancelImprove}
                        promptText="How would you like to improve this section?"
                        placeholder="Describe the improvements you'd like..."
                      />
                    )}
                  </Box>
                )}
              </Box>

              {/* Action buttons - positioned absolutely in top-right */}
              <Box sx={{
                position: 'absolute',
                right: 10,
                top: 10,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5
              }}>
                <ActionButtons
                  item={section}
                  itemIndex={index}
                  featureId={sectionKey}
                  editingFeature={editingSection}
                  isSaving={isSaving}
                  onEdit={() => onEdit(sectionKey, section.content)}
                  onSave={() => onSave(sectionKey)}
                  onCancel={() => onCancel()}
                  onCopy={() => onCopy(section)}
                  onDelete={() => onDelete(section, index)}
                  onImprove={() => onImprove(section, index)}
                  vertical={true}
                          isApproved={isValidated}
                          approvedByType={isValidated ? getValidatorType(section.id) : null}
                />
              </Box>
            </Accordion>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

const SplitViewProjectInfoConversation = ({ conversation, children, isStreaming, streamingContent, projectId, onConversationUpdate }) => {
  const { showNotification } = useNotification();
  const isLocalUpdateRef = useRef(false);

  // Full screen state management
  const {
    isFullScreen,
    toggleFullScreen,
    getLeftPanelStyles,
    getRightPanelStyles
  } = useFullScreen();

  // Use version management for project info
  const {
    versions,
    currentVersionIndex,
    setCurrentVersionIndex,
    hasVersions
  } = useProjectInfoVersions(conversation, isStreaming, streamingContent, isLocalUpdateRef);

  // Filter out preview versions for display
  const displayVersions = useMemo(() =>
    versions.filter(v => !v.isPreview),
    [versions]
  );

  // SIMPLE APPROACH: Get current version directly from versions array
  // This ensures we ALWAYS have the correct version for the current index
  const currentVersion = versions[currentVersionIndex] || null;

  // Use validation hook for database-persisted validation state
  // Task ID is 'project-info' for Project Information sections
  const {
    validatedItems: validatedSections,
    handleValidateItem,
    isItemValidated,
    isLoading: isLoadingValidations,
    currentUserMemberType,
    isAssignedValidator,
    getValidatorType,
  } = useValidation({
    conversationId: conversation?._id,
    projectId: projectId,
    taskId: 'project-info',
    initialState: {}
  });

  // Use improve hook for AI-powered section improvement
  const {
    isImproving,
    improvedContent,
    showImprovementModal,
    currentSection: improveSection,
    showImproveInput,
    improvePrompt,
    setImprovePrompt,
    handleImproveClick,
    handleSendImprove,
    handleCancelImprove,
    closeImprovementModal,
    isSpecificSectionImprovement,
    specificSectionName
  } = useImprove(projectId);

  // useParsedSections hook — single source of truth for section data
  const {
    sections: parsedSections,
    setSections: setParsedSections,
    groupedSections,
    isLoading: isSectionsLoading,
    contentRef,
    refresh: refreshSections,
    markLocalEdit,
  } = useParsedSections({ conversation, isStreaming, streamingContent });

  // State for UI
  const [expandedAccordion, setExpandedAccordion] = useState(null);

  // Edit state
  const [editingSection, setEditingSection] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, section: null, index: null });

  // Track if we're auto-generating
  const [isAutoGenerating, setIsAutoGenerating] = useState(false);
  const hasTriggeredAutoGenerate = useRef(false);

  // Track if user has manually regenerated (clicked refresh button)
  const [hasUserRegenerated, setHasUserRegenerated] = useState(false);

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);

  // Check if conversation has messages
  const hasMessages = conversation?.messages && conversation.messages.length > 0;

  // SIMPLE VERSION SWITCHING: Use pre-parsed sections from version object
  // This ensures consistency - sections are parsed once when version is created
  // and reused when switching versions (no re-parsing that could produce different results)
  //
  // NORMALIZATION RULES:
  // - ONLY normalize during STREAMING (preview versions) - ensures all 12 sections show while AI generates
  // - DO NOT normalize saved/loaded versions - respects user deletions
  // - This ensures deleted sections stay deleted after page refresh
  useEffect(() => {
    // If useParsedSections already has data from backend AND we're not streaming, use it
    if (parsedSections.length > 0 && !isStreaming) {
      return;
    }

    const version = versions[currentVersionIndex];

    // Auto-cancel any active edit when switching versions
    // This prevents unsaved edits from being lost without warning
    if (editingSection) {
      console.log('[Version Switch] Cancelling active edit on version change');
      setEditingSection(null);
      setEditedContent('');
    }

    // Auto-cancel any active improve when switching versions
    if (showImproveInput) {
      console.log('[Version Switch] Cancelling active improve on version change');
      handleCancelImprove();
    }

    console.log('[Version Switch] Index:', currentVersionIndex, 'Version exists:', !!version);

    if (!version) {
      console.log('[Version Switch] No version at index', currentVersionIndex);
      return;
    }

    // Use pre-parsed sections from version object (single source of truth)
    // Fall back to re-parsing only if sections not available
    let sections = version.sections || version.parsedContent;

    // If no pre-parsed sections, parse from content (fallback for older versions)
    if ((!sections || sections.length === 0) && version.content) {
      console.log('[Version Switch] No pre-parsed sections, parsing from content');
      sections = parseProjectInfoSections(version.content);
    }

    console.log('[Version Switch] Raw sections:', sections?.length || 0, 'isPreview:', version.isPreview);

    if (sections && sections.length > 0) {
      // ONLY normalize for preview/streaming versions
      // For saved versions, use sections as-is to respect user deletions
      if (version.isPreview) {
        // Normalize during streaming - ensures all 12 sections visible while AI generates
        const normalizedSections = normalizeSections(sections);
        console.log('[Version Switch] Preview: Normalized to', normalizedSections.length, 'sections');
        setParsedSections(normalizedSections);
      } else {
        // Saved version - use sections directly without normalization
        // This ensures deleted sections stay deleted after page refresh
        console.log('[Version Switch] Saved version: Using', sections.length, 'sections (no normalization)');
        setParsedSections(sections);
      }
    } else if (version.content) {
      // If still no sections but have content, try parsing one more time
      // This handles edge cases where sections might not be stored correctly
      const fallbackSections = parseProjectInfoSections(version.content);
      if (fallbackSections.length > 0) {
        // For saved versions with fallback parsing, don't normalize
        if (version.isPreview) {
          const normalizedFallback = normalizeSections(fallbackSections);
          console.log('[Version Switch] Fallback preview: Normalized to', normalizedFallback.length, 'sections');
          setParsedSections(normalizedFallback);
        } else {
          console.log('[Version Switch] Fallback saved: Using', fallbackSections.length, 'sections');
          setParsedSections(fallbackSections);
        }
      }
    }
  }, [versions, currentVersionIndex, parsedSections, isStreaming]);

  // Helper function to check if sections are valid (must have at least 2 sections)
  const isValidSections = (sections) => {
    if (!sections || sections.length < 2) return false;
    return true;
  };

  // Reset auto-generate flag when conversation changes
  useEffect(() => {
    hasTriggeredAutoGenerate.current = false;
    setParsedSections([]);
    console.log('[Reset] Cleared for new conversation');
  }, [conversation?._id]);

  // Handle single accordion open behavior
  const handleAccordionChange = (sectionKey) => {
    setExpandedAccordion(expandedAccordion === sectionKey ? null : sectionKey);
  };

  /**
   * Create a version-specific validation ID
   * Format: v{versionIndex}-{sectionKey}
   * This ensures validation is specific to each version
   */
  const getVersionSpecificValidationId = (sectionKey) => {
    return `vt${currentVersion?.timestamp || currentVersionIndex}-${sectionKey}`;
  };

  /**
   * Check if a section is validated for the current version
   */
  const isSectionValidated = (sectionKey) => {
    const versionSpecificId = getVersionSpecificValidationId(sectionKey);
    return isItemValidated(versionSpecificId);
  };

  /**
   * Validate section - persists to database with version-specific ID
   * @param {string} sectionKey - The section ID
   * @param {string} sectionTitle - The section title for notification
   */
  const handleValidate = (sectionKey, sectionTitle = '') => {
    const versionSpecificId = getVersionSpecificValidationId(sectionKey);
    handleValidateItem(versionSpecificId, sectionTitle || sectionKey);
  };

  // Edit handlers
  const handleEdit = (sectionKey, content) => {
    setEditingSection(sectionKey);
    setEditedContent(content || '');
  };

  const handleSave = async (sectionKey) => {
    setIsSaving(true);
    try {
      // Find and update the section
      const updatedSections = parsedSections.map(section => {
        if ((section.id || `section-${parsedSections.indexOf(section)}`) === sectionKey) {
          return { ...section, content: editedContent };
        }
        return section;
      });

      // Set flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;

      // Update local state immediately for responsive UI
      setParsedSections(updatedSections);

      // Reconstruct markdown content using # (H1) headers for sections
      // This matches the format expected by the AI and parsers
      let newContent = '';
      updatedSections.forEach((s, idx) => {
        if (idx > 0) newContent += '\n';
        // Use H1 (#) for main sections - matches MANDATORY_SECTIONS format
        newContent += `# ${s.title}\n\n${s.content || ''}\n`;
      });

      // VERSION-SPECIFIC UPDATE: Update only the current version's message
      // This ensures editing in Version 1 doesn't affect Version 2, etc.
      if (conversation?.messages && currentVersion) {
        const updatedMessages = [...conversation.messages];
        let messageUpdated = false;

        // Use messageIndex first - most reliable way to find the correct message
        if (currentVersion.messageIndex !== undefined) {
          const idx = currentVersion.messageIndex;
          if (updatedMessages[idx] && updatedMessages[idx].role === 'assistant') {
            updatedMessages[idx] = {
              ...updatedMessages[idx],
              content: newContent.trim()
            };
            messageUpdated = true;
            console.log('✅ Edit: Updated message by messageIndex at index', idx);
          }
        }

        // Fallback: Try to match by timestamp
        if (!messageUpdated && currentVersion.timestamp) {
          for (let i = 0; i < updatedMessages.length; i++) {
            if (updatedMessages[i].role === 'assistant' &&
                updatedMessages[i].createdAt === currentVersion.timestamp) {
              updatedMessages[i] = {
                ...updatedMessages[i],
                content: newContent.trim()
              };
              messageUpdated = true;
              console.log('✅ Edit: Updated message by timestamp at index', i);
              break;
            }
          }
        }

        // Last resort: Find the last assistant message
        if (!messageUpdated) {
          console.warn('⚠️ Edit: No version match, updating last assistant message');
          for (let i = updatedMessages.length - 1; i >= 0; i--) {
            if (updatedMessages[i].role === 'assistant') {
              updatedMessages[i] = {
                ...updatedMessages[i],
                content: newContent.trim()
              };
              messageUpdated = true;
              break;
            }
          }
        }

        // Save to backend via conversationService
        if (messageUpdated) {
          const conversationData = {
            messages: updatedMessages.map(msg => ({
              role: msg.role,
              content: msg.content,
              attachments: msg.attachments || [],
              createdAt: msg.createdAt || new Date().toISOString()
            }))
          };

          await conversationService.saveConversation(conversation._id, conversationData);
          console.log('✅ Edit: Saved conversation with updated messages');

          // Update LHS immediately by calling onConversationUpdate with updated messages
          if (onConversationUpdate) {
            await onConversationUpdate(conversation._id, updatedMessages);
            console.log('✅ Edit: Called onConversationUpdate to sync LHS');
          }
        }
      }

      setEditingSection(null);
      setEditedContent('');

      showNotification({
        message: 'Section updated successfully!',
        type: 'success'
      });
    } catch (error) {
      console.error('Error saving section:', error);
      showNotification({
        message: 'Failed to save section',
        type: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingSection(null);
    setEditedContent('');
  };

  // Copy handler
  const handleCopy = (section) => {
    navigator.clipboard.writeText(section.content || '');
    showNotification({
      message: 'Content copied to clipboard!',
      type: 'success'
    });
  };

  // Delete handlers
  const handleDeleteClick = (section, index) => {
    setDeleteConfirmation({ open: true, section, index });
  };

  const handleDeleteConfirm = async () => {
    const { section, index } = deleteConfirmation;
    setDeleteConfirmation({ open: false, section: null, index: null });

    if (!section) return;

    try {
      // Remove the section from parsedSections
      const updatedSections = parsedSections.filter((_, idx) => idx !== index);

      // Recalculate isFirstInCategory
      const seenCategories = new Set();
      updatedSections.forEach(s => {
        if (s.category && !seenCategories.has(s.category)) {
          s.isFirstInCategory = true;
          seenCategories.add(s.category);
        } else {
          s.isFirstInCategory = false;
        }
      });

      // Set flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;

      // Update local state immediately for responsive UI
      setParsedSections(updatedSections);

      // Close accordion if it was the deleted one
      setExpandedAccordion(null);

      // Reconstruct markdown content using # (H1) headers for sections
      // This matches the format expected by the AI and parsers
      let newContent = '';
      updatedSections.forEach((s, idx) => {
        if (idx > 0) newContent += '\n';
        // Use H1 (#) for main sections - matches MANDATORY_SECTIONS format
        newContent += `# ${s.title}\n\n${s.content || ''}\n`;
      });

      // VERSION-SPECIFIC UPDATE: Update only the current version's message
      // This ensures deletion in Version 1 doesn't affect Version 2, etc.
      if (conversation?.messages && currentVersion) {
        const updatedMessages = [...conversation.messages];
        let messageUpdated = false;

        // Use messageIndex first - most reliable way to find the correct message
        if (currentVersion.messageIndex !== undefined) {
          const idx = currentVersion.messageIndex;
          if (updatedMessages[idx] && updatedMessages[idx].role === 'assistant') {
            updatedMessages[idx] = {
              ...updatedMessages[idx],
              content: newContent.trim()
            };
            messageUpdated = true;
            console.log('✅ Delete: Updated message by messageIndex at index', idx);
          }
        }

        // Fallback: Try to match by timestamp
        if (!messageUpdated && currentVersion.timestamp) {
          for (let i = 0; i < updatedMessages.length; i++) {
            if (updatedMessages[i].role === 'assistant' &&
                updatedMessages[i].createdAt === currentVersion.timestamp) {
              updatedMessages[i] = {
                ...updatedMessages[i],
                content: newContent.trim()
              };
              messageUpdated = true;
              console.log('✅ Delete: Updated message by timestamp at index', i);
              break;
            }
          }
        }

        // Last resort: Find the last assistant message
        if (!messageUpdated) {
          console.warn('⚠️ Delete: No version match, updating last assistant message');
          for (let i = updatedMessages.length - 1; i >= 0; i--) {
            if (updatedMessages[i].role === 'assistant') {
              updatedMessages[i] = {
                ...updatedMessages[i],
                content: newContent.trim()
              };
              messageUpdated = true;
              break;
            }
          }
        }

        // Save to backend via conversationService
        if (messageUpdated) {
          const conversationData = {
            messages: updatedMessages.map(msg => ({
              role: msg.role,
              content: msg.content,
              attachments: msg.attachments || [],
              createdAt: msg.createdAt || new Date().toISOString()
            }))
          };

          await conversationService.saveConversation(conversation._id, conversationData);
          console.log('✅ Delete: Saved conversation with updated messages');

          // Update LHS immediately by calling onConversationUpdate with updated messages
          if (onConversationUpdate) {
            await onConversationUpdate(conversation._id, updatedMessages);
            console.log('✅ Delete: Called onConversationUpdate to sync LHS');
          }
        }
      }

      showNotification({
        message: `"${section.title}" deleted successfully`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting section:', error);
      showNotification({
        message: `Failed to delete "${section.title}"`,
        type: 'error'
      });
    }
  };

  // Improve handlers - using useImprove hook for AI-powered improvement
  const handleImprove = (section, index) => {
    // Create a section object compatible with useImprove hook
    const sectionForImprove = {
      ...section,
      id: section.id,
      title: section.title,
      content: section.content,
      rawContent: section.content,
      type: 'project-info' // Identify as project info section
    };
    handleImproveClick(sectionForImprove);
  };

  // Wrapper for sending improve request
  const handleSendSectionImprove = (section) => {
    const sectionForImprove = {
      ...section,
      id: section.id,
      title: section.title,
      content: section.content,
      rawContent: section.content,
      type: 'project-info'
    };
    handleSendImprove(sectionForImprove);
  };

  // Handle update from improvement modal - same pattern as Edit/Delete
  const handleUpdateImprovedContent = async () => {
    if (!improveSection || !improvedContent) return;

    try {
      // Handle remove action — delete section from artifact
      if (improveSection.action === 'remove') {
        
      // Persist to ParsedResponse so changes survive refresh
      try {
        const ts = conversation?.messages?.find(m => m.role === 'assistant')?.createdAt;
        if (ts) {
          const conversationService = (await import('../../services/conversationService')).default;
          await conversationService.updateParsedSections(
            conversation._id || conversation.id,
            ts,
            [] // empty — backend will reconstruct from the saved conversation message
          );
        }
      } catch (e) { console.warn('ParsedResponse save failed:', e); }

      closeImprovementModal();
        showNotification({ message: `Removed: ${improveSection.title}`, type: 'success' });
        // Trigger a conversation refresh to pick up the change
        if (typeof onConversationUpdate === 'function') onConversationUpdate();
        return;
      }


      // Use merged content if available, otherwise use improved content directly
      const contentToUse = improveSection.mergedContent || improvedContent;

      // Find and update only the improved section
      const updatedSections = parsedSections.map(section => {
        if (section.id === improveSection.id) {
          return {
            ...section,
            content: contentToUse
          };
        }
        return section;
      });

      // Set flag to prevent fetch from overwriting local update
      markLocalEdit();
      isLocalUpdateRef.current = true;

      // Update local state immediately for responsive UI
      setParsedSections(updatedSections);

      // Reconstruct markdown content using # (H1) headers for sections
      let newContent = '';
      updatedSections.forEach((s, idx) => {
        if (idx > 0) newContent += '\n';
        newContent += `# ${s.title}\n\n${s.content || ''}\n`;
      });

      // VERSION-SPECIFIC UPDATE: Update only the current version's message
      if (conversation?.messages && currentVersion) {
        const updatedMessages = [...conversation.messages];
        let messageUpdated = false;

        // Use messageIndex first - most reliable way to find the correct message
        if (currentVersion.messageIndex !== undefined) {
          const idx = currentVersion.messageIndex;
          if (updatedMessages[idx] && updatedMessages[idx].role === 'assistant') {
            updatedMessages[idx] = {
              ...updatedMessages[idx],
              content: newContent.trim()
            };
            messageUpdated = true;
            console.log('✅ Improve: Updated message by messageIndex at index', idx);
          }
        }

        // Fallback: Try to match by timestamp
        if (!messageUpdated && currentVersion.timestamp) {
          for (let i = 0; i < updatedMessages.length; i++) {
            if (updatedMessages[i].role === 'assistant' &&
                updatedMessages[i].createdAt === currentVersion.timestamp) {
              updatedMessages[i] = {
                ...updatedMessages[i],
                content: newContent.trim()
              };
              messageUpdated = true;
              console.log('✅ Improve: Updated message by timestamp at index', i);
              break;
            }
          }
        }

        // Last resort: Find the last assistant message
        if (!messageUpdated) {
          console.warn('⚠️ Improve: No version match, updating last assistant message');
          for (let i = updatedMessages.length - 1; i >= 0; i--) {
            if (updatedMessages[i].role === 'assistant') {
              updatedMessages[i] = {
                ...updatedMessages[i],
                content: newContent.trim()
              };
              messageUpdated = true;
              break;
            }
          }
        }

        // Save to backend via conversationService
        if (messageUpdated) {
          const conversationData = {
            messages: updatedMessages.map(msg => ({
              role: msg.role,
              content: msg.content,
              attachments: msg.attachments || [],
              createdAt: msg.createdAt || new Date().toISOString()
            }))
          };

          await conversationService.saveConversation(conversation._id, conversationData);
          console.log('✅ Improve: Saved conversation with updated messages');

          // Update LHS immediately by calling onConversationUpdate with updated messages
          if (onConversationUpdate) {
            await onConversationUpdate(conversation._id, updatedMessages);
            console.log('✅ Improve: Called onConversationUpdate to sync LHS');
          }
        }
      }

      // Close the improvement modal
      closeImprovementModal();

      showNotification({
        message: `"${improveSection.title}" improved successfully`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error updating improved content:', error);
      showNotification({
        message: 'Failed to update improved content',
        type: 'error'
      });
    }
  };

  // Auto-generate project info (silent - no success notification)
  const handleAutoGenerateProjectInfo = async () => {
    if (!conversation?._id) return;

    setIsAutoGenerating(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8003/api';
      const response = await axios.post(
        `${baseUrl}/projects/${projectId}/generate-info`,
        { conversationId: conversation._id },
        { headers: getAuthHeaders() }
      );

      // Use the response to immediately update the parsed sections
      // DO NOT normalize - this is a saved version, respect any deletions
      if (response.data && response.data.content) {
        const sections = parseProjectInfoSections(response.data.content);
        if (sections && sections.length > 0) {
          setParsedSections(sections);
        }
      }
      // No success notification for auto-generation - it's automatic
    } catch (error) {
      console.error('Error auto-generating project info:', error);
      // Don't show error for auto-generation - it might be expected if no content yet
    } finally {
      setIsAutoGenerating(false);
    }
  };

  // Manual refresh handler
  const handleRefreshProjectInfo = async () => {
    if (!projectId || !conversation?._id) return;

    setIsAutoGenerating(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8003/api';
      const response = await axios.post(
        `${baseUrl}/projects/${projectId}/generate-info`,
        { conversationId: conversation._id },
        { headers: getAuthHeaders() }
      );

      if (response.data) {
        // Use the response to immediately update the parsed sections
        // DO NOT normalize - this is a saved version, respect any deletions
        if (response.data.content) {
          const sections = parseProjectInfoSections(response.data.content);
          if (sections && sections.length > 0) {
            setParsedSections(sections);
          }
        }
        // Mark that user has manually regenerated - enables version dropdown
        setHasUserRegenerated(true);
        showNotification({
          message: 'Project information regenerated successfully!',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error regenerating project info:', error);
      showNotification({
        message: 'Failed to regenerate project information',
        type: 'error'
      });
    } finally {
      setIsAutoGenerating(false);
    }
  };

  /**
   * Export validated sections from ALL versions as markdown
   *
   * Rules:
   * - Iterates through ALL versions (not just current)
   * - Collects ONLY validated accordion sections from each version
   * - Excludes unvalidated and deleted sections
   * - Skips versions with zero validated sections
   * - Does NOT call LLM or modify stored data
   */
  const handleReExtract = async () => {
    if (!conversation?._id) return;

    // Get current version's message timestamp
    const currentVersion = versions?.[currentVersionIndex];
    if (!currentVersion?.messageTimestamp) {
      showNotification({ message: 'No version selected for re-extraction', type: 'warning' });
      return;
    }

    setIsExporting(true);
    try {
      const result = await documentExportService.reExtractSections(
        conversation._id,
        currentVersion.messageTimestamp
      );
      showNotification({
        message: `Re-extracted ${result.data?.sections_count || 0} sections`,
        type: 'success'
      });
      // Reload conversation to pick up new parsed sections
      if (onConversationUpdate) {
        onConversationUpdate();
      }
    } catch (error) {
      console.error('Re-extraction failed:', error);
      showNotification({ message: 'Re-extraction failed', type: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportValidatedSections = () => {
    try {
      setIsExporting(true);

      // Filter out preview versions for export
      const exportVersions = versions.filter(v => !v.isPreview);

      if (exportVersions.length === 0) {
        showNotification({
          message: 'No versions available to export',
          type: 'warning'
        });
        setIsExporting(false);
        return;
      }

      // Collect validated sections from ALL versions
      const exportContent = [];
      let totalValidatedSections = 0;

      exportVersions.forEach((version, versionIndex) => {
        // Get sections from this version
        const versionSections = version.sections || version.parsedContent || [];

        if (!versionSections || versionSections.length === 0) {
          return; // Skip versions without sections
        }

        // Collect ALL sections with content for export (validation not required)
        const sectionsForVersion = versionSections.filter(
          section => section.content && section.content.trim().length > 0
        );

        if (sectionsForVersion.length > 0) {
          if (exportVersions.length > 1) {
            exportContent.push(`\n---\n\n## Version ${versionIndex + 1}\n`);
          }

          sectionsForVersion.forEach(section => {
            exportContent.push(`# ${section.title}\n\n${section.content}\n`);
          });

          totalValidatedSections += sectionsForVersion.length;
        }
      });

      if (totalValidatedSections === 0) {
        showNotification({
          message: 'No sections with content to export.',
          type: 'warning'
        });
        setIsExporting(false);
        return;
      }

      // Generate the final markdown content
      const markdownContent = exportContent.join('\n').trim();
      return { markdownContent, totalValidatedSections };
    } catch (error) {
      console.error('Error collecting export content:', error);
      showNotification({ message: 'Failed to export project information', type: 'error' });
      setIsExporting(false);
      return null;
    }
  };

  // Convert markdown to simple HTML
  const markdownToHtml = (md) => {
    let html = md
      .replace(/^### (.*$)/gm, '<h3>$1</h3>')
      .replace(/^## (.*$)/gm, '<h2>$1</h2>')
      .replace(/^# (.*$)/gm, '<h1>$1</h1>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^\- (.*$)/gm, '<li>$1</li>')
      .replace(/^\• (.*$)/gm, '<li>$1</li>')
      .replace(/\n{2,}/g, '</p><p>')
      .replace(/---/g, '<hr/>');
    // Wrap lists
    html = html.replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>');
    return `<p>${html}</p>`.replace(/<p><\/p>/g, '');
  };

  const triggerDownload = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
  };

  const handleExportFormat = async (format) => {
    setDownloadMenuAnchor(null);
    setIsExporting(true);

    const result = handleExportValidatedSections();
    if (!result) return;

    const { markdownContent, totalValidatedSections } = result;
    const date = new Date().toISOString().split('T')[0];
    const taskName = conversation?.task_id || conversation?.taskId || 'project-info';
    const baseFilename = `${taskName}-${date}`;

    try {
      // All formats go through the backend unified export API
      const docTitle = baseFilename.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      const response = await api.post(
        `/export/${format}/content`,
        {
          content: markdownContent,
          title: docTitle,
          include_toc: true,
          include_cover: true,
          include_diagrams: true,
          project_id: projectId,
          metadata: { subtitle: taskName.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }
        },
        { responseType: 'blob' }
      );

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${baseFilename}.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      // Trigger download
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(url); }, 100);

      showNotification({
        message: `Exported as ${format.toUpperCase()} (${totalValidatedSections} validated section${totalValidatedSections > 1 ? 's' : ''})`,
        type: 'success'
      });
    } catch (error) {
      console.error(`Error exporting as ${format}:`, error);
      showNotification({ message: `Failed to export as ${format.toUpperCase()}`, type: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  // Determine what to show
  const showSplitView = hasMessages;
  const showLoading = isStreaming || isAutoGenerating;
  const showContent = parsedSections.length > 0;

  // Debug: Log what's being rendered
  console.log('[RENDER] currentVersionIndex:', currentVersionIndex, 'parsedSections count:', parsedSections.length);
  if (parsedSections.length > 0) {
    console.log('[RENDER] First section title:', parsedSections[0]?.title);
  }

  // If no messages yet, show single view (full width conversation)
  if (!showSplitView) {
    return (
      <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
        {children}
      </Box>
    );
  }

  // Show split view when there are messages
  return (
    <Box sx={{ display: 'flex', height: '100%', width: '100%', overflow: 'hidden' }}>
      {/* Left Panel - Conversation */}
      <Box sx={{
        ...getLeftPanelStyles(),
        minHeight: 0
      }}>
        {children}
      </Box>

      {/* Right Panel - Project Information */}
      <Box sx={{
        ...getRightPanelStyles(),
        width: isFullScreen ? '100%' : '50%',
        height: '97%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        overflow: 'auto',
        border: '1px solid #fff',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        p: 3
      }}>
        {/* Header - Sticky */}
        <Box sx={{
          p: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          flexShrink: 0
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Project Information</Typography>
              <Typography variant="body2" color="text.secondary">
                {showLoading ? 'Generating project information...' : 'Dynamically extracted from conversation'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Version Selector - show when multiple versions exist */}
              {displayVersions.length > 1 && (
                <VersionSelector
                  versions={displayVersions}
                  currentVersionIndex={currentVersionIndex}
                  onVersionChange={setCurrentVersionIndex}
                  disabled={isStreaming || isAutoGenerating}
                />
              )}
              {/* Refresh button */}
              <IconButton
                onClick={handleRefreshProjectInfo}
                disabled={isStreaming || isAutoGenerating}
                title="Regenerate project info"
              >
                {isAutoGenerating ? (
                  <CircularProgress size={20} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
              {/* Export button - opens format selection menu */}
              <PermissionGate permission="conversation.content.download">
                <IconButton
                  onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                  disabled={isStreaming || isAutoGenerating || isExporting}
                  title="Export validated sections"
                >
                  {isExporting ? (
                    <CircularProgress size={20} />
                  ) : (
                    <DownloadIcon />
                  )}
                </IconButton>
                <Menu
                  anchorEl={downloadMenuAnchor}
                  open={Boolean(downloadMenuAnchor)}
                  onClose={() => setDownloadMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => handleExportFormat('md')}>
                  <ListItemIcon><MarkdownIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Markdown (.md)</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleExportFormat('pdf')}>
                  <ListItemIcon><PdfIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>PDF (.pdf)</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleExportFormat('docx')}>
                  <ListItemIcon><DocxIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Word (.docx)</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleExportFormat('html')}>
                  <ListItemIcon><HtmlIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>HTML (.html)</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleExportFormat('txt')}>
                  <ListItemIcon><TxtIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Plain Text (.txt)</ListItemText>
                </MenuItem>
              </Menu>
              </PermissionGate>
              {/* Full screen toggle button */}
              <FullScreenButton
                isFullScreen={isFullScreen}
                onToggle={toggleFullScreen}
                tooltipEnter="Expand to full screen"
                tooltipExit="Exit full screen"
              />
            </Box>
          </Box>
        </Box>

        {/* Content - Scrollable */}
        <Box sx={{
          flex: 1,
          overflow: 'auto',
        }}>
          {showLoading ? (
            <LoadingPanel />
          ) : showContent ? (
            <DynamicProjectInfoAccordion
              key={`accordion-v${currentVersionIndex}`}
              sections={parsedSections}
              expandedAccordion={expandedAccordion}
              onAccordionChange={handleAccordionChange}
              isSectionValidated={isSectionValidated}
              onValidate={handleValidate}
              memberType={currentUserMemberType}
              isAssignedValidator={isAssignedValidator}
              getValidatorType={getValidatorType}
              onEdit={handleEdit}
              onSave={handleSave}
              onCancel={handleCancel}
              onImprove={handleImprove}
              onCopy={handleCopy}
              onDelete={handleDeleteClick}
              editingSection={editingSection}
              editedContent={editedContent}
              setEditedContent={setEditedContent}
              isSaving={isSaving}
              // Improve props
              showImproveInput={showImproveInput}
              improvePrompt={improvePrompt}
              setImprovePrompt={setImprovePrompt}
              onSendImprove={handleSendSectionImprove}
              onCancelImprove={handleCancelImprove}
            />
          ) : (
            <LoadingPanel />
          )}
        </Box>
      </Box>

      {/* Confirmation Dialog for Delete */}
      <ConfirmationDialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, section: null, index: null })}
        title="Delete Section"
        message={`Are you sure you want to delete "${deleteConfirmation.section?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        severity="warning"
        height="auto"
        onConfirm={handleDeleteConfirm}
      />

      {/* Improvement Modal - shows before/after comparison */}
      <ImprovementModal
        open={showImprovementModal}
        onClose={closeImprovementModal}
        onUpdate={handleUpdateImprovedContent}
        section={improveSection}
        improvedContent={improvedContent}
        isGenerating={isImproving}
        isSpecificSection={isSpecificSectionImprovement}
        specificSectionName={specificSectionName}
      />
    </Box>
  );
};

export default SplitViewProjectInfoConversation;
